import { useMemo, useState } from 'react'

import { useFacilitySearch } from './useFacilitySearch'
import { useDebounce } from 'use-debounce'

import { FacilityFilterInput } from '@/graphql/generated/schemas'
import {
  FacilityOptionWithAddressI,
  getIncidentFacilityOptionsWithAddress,
} from '@/utils/forms/optionBuilders'
import { SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

interface UseFacilitySearchOptionsIProps {
  filter?: FacilityFilterInput
}

interface UseFacilitySearchOptionsResultIProps {
  facilityOptions: FacilityOptionWithAddressI[]
  isFacilitiesLoading: boolean
  isFacilitiesError: boolean
  setFacilityName: (value: string) => void
}

/**
 * Debounced facility search + option building, shared by every facility
 * field in this ticket's create/edit modals (Floor, Device, Case, SOP,
 * Managed Accounts) — each previously hand-rolled the same
 * debounce/useFacilitySearch/getIncidentFacilityOptionsWithAddress block.
 * Deliberately doesn't compute a subtitle/caption cue: callers render that
 * differently (single-select formatOptionLabel vs. multi-select caption),
 * so that mapping stays at the call site.
 */
export const useFacilitySearchOptions = ({
  filter,
}: UseFacilitySearchOptionsIProps = {}): UseFacilitySearchOptionsResultIProps => {
  const [facilityName, setFacilityName] = useState('')
  const [debouncedFacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )
  const {
    facilitiesData,
    isFacilitiesLoading,
    isError: isFacilitiesError,
  } = useFacilitySearch({ searchQuery: debouncedFacilityName, filter })

  const facilityOptions = useMemo(
    () =>
      getIncidentFacilityOptionsWithAddress(
        facilitiesData,
        true,
        debouncedFacilityName
      ),
    [facilitiesData, debouncedFacilityName]
  )

  return {
    facilityOptions,
    isFacilitiesLoading,
    isFacilitiesError,
    setFacilityName,
  }
}
